import { z } from "zod";
import { channelIdSchema } from "./channels.js";
import { guestAccessSchema } from "./organization-members.js";
/** Whether an organisation admits guests; off until an admin turns it on. */
export const workspaceGuestSettingsSchema = z.object({
    organisationId: z.string().trim().min(1),
    /** Whether this server admits guests at all; without it nothing below applies. */
    available: z.boolean(),
    allowGuests: z.boolean(),
    updatedAt: z.number().nullable(),
});
export const workspaceGuestSettingsResponseSchema = z.object({ settings: workspaceGuestSettingsSchema });
export const putWorkspaceGuestSettingsInputSchema = z.object({ allowGuests: z.boolean() }).strict();
/** An invitation into an external channel, waiting for a guest with this email to sign in. */
export const channelGuestInvitationSchema = z.object({
    id: z.string().trim().min(1),
    channelId: channelIdSchema,
    email: z.string().trim().min(3).max(320),
    access: guestAccessSchema,
    invitedBy: z.string().trim().min(1),
    createdAt: z.number(),
});
export const createChannelGuestInvitationInputSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(320),
    access: guestAccessSchema,
}).strict();
export const channelGuestInvitationResponseSchema = z.object({ invitation: channelGuestInvitationSchema });
/** A guest who has joined the channel. Their email is the one they were invited with, when known. */
export const channelGuestSchema = z.object({
    userId: z.string().trim().min(1),
    access: guestAccessSchema,
    email: z.string().nullable(),
    joinedAt: z.number(),
});
export const listChannelGuestsResponseSchema = z.object({
    guests: z.array(channelGuestSchema),
    invitations: z.array(channelGuestInvitationSchema),
});
/** The most people a guest's directory lists; a guest's channels hold far fewer. */
export const GUEST_PEOPLE_LIMIT = 1000;
/**
 * The people a guest works with: everyone who belongs to an external channel
 * the guest is in, the guest included. Names come from the identity provider;
 * the control plane holds only who they are.
 */
export const workspaceGuestPeopleResponseSchema = z.object({
    people: z.array(z.object({ userId: z.string().trim().min(1) })).max(GUEST_PEOPLE_LIMIT),
});
/**
 * Whether a guest still belongs to a channel: a membership, archived channels
 * included, or an invitation waiting for an email they joined with. A guest
 * with none leaves the organisation.
 */
export const workspaceGuestStandingResponseSchema = z.object({
    userId: z.string().trim().min(1),
    inChannels: z.boolean(),
});
/**
 * The most guests one request may name. Each departure takes a few provider
 * calls, so a channel with more guests is settled over several requests, each
 * well inside the app's minute-long wait for admin-control.
 */
export const GUEST_DEPARTURES_LIMIT = 20;
/**
 * Guests just removed from a channel, and emails whose invitation to one was
 * just withdrawn. A guest no channel holds leaves the organisation; an email
 * no channel still invites loses its invitation to the organisation.
 */
export const guestDeparturesInputSchema = z.object({
    userIds: z.array(z.string().trim().min(1).max(200)).default([]),
    emails: z.array(z.string().trim().toLowerCase().email().max(320)).default([]),
}).strict().refine((input) => input.userIds.length + input.emails.length >= 1
    && input.userIds.length + input.emails.length <= GUEST_DEPARTURES_LIMIT, { message: `Name between 1 and ${GUEST_DEPARTURES_LIMIT} guests or emails.` });
/**
 * What became of each guest: they left the organisation, they still belong
 * to a channel, they are no guest here (or have already gone), or their
 * departure could not be completed.
 */
export const guestDepartureOutcomeSchema = z.enum(["left", "in_channels", "not_guest", "failed"]);
/**
 * What became of each email's invitation to the organisation: withdrawn, kept
 * because a channel still invites the email, none was waiting (or it was a
 * member's), or withdrawing it could not be completed.
 */
export const guestInvitationWithdrawalOutcomeSchema = z.enum(["withdrawn", "invited_elsewhere", "not_invited", "failed"]);
export const guestDeparturesResponseSchema = z.object({
    departures: z.array(z.object({ userId: z.string(), outcome: guestDepartureOutcomeSchema })),
    invitations: z.array(z.object({ email: z.string(), outcome: guestInvitationWithdrawalOutcomeSchema })),
});
/**
 * Whether any channel still invites an email, named by the SHA-256 of the
 * lowercase address so the address itself stays out of the request.
 */
export const workspaceGuestInvitationStandingResponseSchema = z.object({
    emailSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    invited: z.boolean(),
});
